<template>
  <div>
    <header class="vm-page-header">
      <div>
        <h1>Financeiro</h1>
        <span class="vm-page-header__sub">
          Conta bancária para recebimento dos repasses
        </span>
      </div>
      <div class="vm-page-header__actions">
        <router-link
          :to="{ name: 'FinanceiroDashboard' }"
          class="btn btn--outline btn--sm"
        >
          Visão geral
        </router-link>
      </div>
    </header>

    <div class="fin-body">
      <div class="tabs">
        <router-link
          class="tab"
          active-class="tab--active"
          :to="{ name: 'FinanceiroDashboard' }"
          exact
        >
          Visão geral
        </router-link>
        <router-link
          class="tab"
          active-class="tab--active"
          :to="{ name: 'FinanceiroExtrato' }"
        >
          Extrato
        </router-link>
        <router-link
          class="tab"
          active-class="tab--active"
          :to="{ name: 'FinanceiroConta' }"
        >
          Conta bancária
        </router-link>
      </div>

      <!-- Skeleton -->
      <div v-if="isLoading" class="fin-conta-grid">
        <div class="card vm-skel" style="height:220px" />
        <div class="card" style="padding:var(--space-6)">
          <div v-for="i in 5" :key="i" style="margin-bottom:var(--space-4)">
            <div
              class="vm-skel"
              style="width:40%;height:12px;margin-bottom:var(--space-2)"
            />
            <div
              class="vm-skel"
              style="width:100%;height:40px;border-radius:var(--radius-sm)"
            />
          </div>
        </div>
      </div>

      <div v-else class="fin-conta-grid">
        <!-- Coluna esquerda -->
        <div class="stack" style="gap:var(--space-4)">
          <!-- Cartão visual (com conta) -->
          <div v-if="hasBankAccount" class="fin-bank-card">
            <div
              class="row"
              style="justify-content:space-between;align-items:center"
            >
              <div class="avatar avatar--md fin-bank-icon">
                <svg
                  width="22"
                  height="22"
                  viewBox="0 0 24 24"
                  fill="none"
                  aria-hidden="true"
                >
                  <path
                    d="M3 10l9-6 9 6M5 10v9h14v-9M9 19v-5h6v5"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </div>
              <span class="badge badge--success badge--no-dot">Verificada</span>
            </div>
            <div class="fin-bank-name">{{ bankAccount.bank_name }}</div>
            <div class="fin-bank-type num">
              Banco {{ bankAccount.bank_number }} · Conta corrente
            </div>
            <div class="fin-bank-details">
              <div class="stack" style="gap:1px">
                <span class="fin-bank-label">Agência</span>
                <span class="fin-bank-value num">
                  {{ bankAccount.agency_number }}
                </span>
              </div>
              <div class="stack" style="gap:1px">
                <span class="fin-bank-label">Conta</span>
                <span class="fin-bank-value num">
                  {{ bankAccount.account_number
                  }}{{
                    bankAccount.account_check_number
                      ? "-" + bankAccount.account_check_number
                      : ""
                  }}
                </span>
              </div>
            </div>
          </div>

          <!-- Estado vazio (sem conta) -->
          <div
            v-else
            class="card"
            style="padding:var(--space-8) var(--space-6);display:flex;align-items:center;justify-content:center;min-height:220px"
          >
            <div class="vm-empty">
              <div class="vm-empty-ill">
                <svg
                  width="32"
                  height="32"
                  viewBox="0 0 24 24"
                  fill="none"
                  aria-hidden="true"
                >
                  <path
                    d="M3 10l9-6 9 6M5 10v9h14v-9M9 19v-5h6v5"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </div>
              <h3>Nenhuma conta cadastrada</h3>
              <p>
                Cadastre uma conta bancária para receber os repasses das suas
                consultas.
              </p>
              <button
                class="btn btn--primary"
                style="margin-top:var(--space-4)"
                @click="openBankAccountModal"
              >
                Cadastrar conta bancária
              </button>
            </div>
          </div>

          <!-- Notice de repasse -->
          <div
            class="card"
            style="background:var(--color-success-light);border-color:transparent;padding:var(--space-4);display:flex;align-items:flex-start;gap:var(--space-3)"
          >
            <div
              class="avatar avatar--sm"
              style="background:var(--color-success);color:#fff;flex:none"
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                aria-hidden="true"
              >
                <path
                  d="M20 6L9 17l-5-5"
                  stroke="currentColor"
                  stroke-width="2.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </div>
            <p
              style="font-size:var(--font-size-xs);color:var(--color-gray-600);margin:0;line-height:1.5"
            >
              Os repasses (80% por consulta paga) são creditados nesta conta a
              cada ciclo de repasse.
            </p>
          </div>

          <!-- Remover conta -->
          <button
            v-if="hasBankAccount"
            class="btn btn--outline btn--sm"
            style="color:var(--color-danger);border-color:var(--color-gray-300)"
            :disabled="removing"
            @click="confirmDelete"
          >
            <b-spinner v-if="removing" small />
            {{ removing ? "Removendo..." : "Remover conta bancária" }}
          </button>
        </div>

        <!-- Coluna direita: Dados da conta -->
        <div class="card" style="padding:var(--space-6)">
          <div
            class="row"
            style="justify-content:space-between;align-items:center;margin-bottom:var(--space-4)"
          >
            <span class="fieldset-title" style="margin:0">Dados da conta</span>
            <button
              v-if="hasBankAccount"
              class="btn btn--ghost btn--sm"
              style="color:var(--color-primary);font-size:var(--font-size-xs);font-weight:var(--fw-bold)"
              @click="openBankAccountModal"
            >
              Alterar
            </button>
          </div>

          <div v-if="hasBankAccount" class="fieldset">
            <div class="field">
              <label>Banco</label>
              <div class="input">{{ bankAccount.bank_name }}</div>
            </div>
            <div class="two-col">
              <div class="field">
                <label>Agência</label>
                <div class="input num">{{ bankAccount.agency_number }}</div>
              </div>
              <div class="field">
                <label>Conta · dígito</label>
                <div class="input num">
                  {{ bankAccount.account_number
                  }}{{
                    bankAccount.account_check_number
                      ? "-" + bankAccount.account_check_number
                      : ""
                  }}
                </div>
              </div>
            </div>
            <div class="field">
              <label>Tipo de conta</label>
              <div class="input">Conta corrente</div>
            </div>
            <div class="field">
              <label>Titular</label>
              <div class="input">{{ userName }}</div>
            </div>
            <div class="field">
              <label>CPF do titular</label>
              <div class="input">{{ userCpf }}</div>
            </div>
          </div>

          <div v-else>
            <p
              style="font-size:var(--font-size-sm);color:var(--color-gray-400);line-height:1.5"
            >
              Nenhuma conta cadastrada. Adicione uma conta ao lado para começar
              a receber repasses.
            </p>
          </div>

          <!-- Notice de aviso -->
          <div
            class="card"
            style="background:var(--color-warning-light);border-color:transparent;padding:var(--space-3) var(--space-4);margin-top:var(--space-4);display:flex;align-items:flex-start;gap:var(--space-2)"
          >
            <span
              style="color:var(--color-warning);font-weight:var(--fw-bold);font-size:var(--font-size-md);line-height:1.4;flex:none"
            >
              !
            </span>
            <p
              style="font-size:var(--font-size-xs);color:var(--color-warning);margin:0;line-height:1.5"
            >
              O CPF do titular deve ser o mesmo do cadastro profissional.
              Alterações passam por nova verificação.
            </p>
          </div>
        </div>
      </div>

      <!-- Modal de cadastro -->
      <b-modal
        id="bankAccount"
        ref="bankAccount"
        size="lg"
        centered
        hide-footer
        hide-header
      >
        <BankAccountForm @bankAccountCreated="onBankAccountCreated" />
      </b-modal>
    </div>
  </div>
</template>

<script>
import BankAccountForm from "../components/BankAccountForm";
import Swal from "sweetalert2";

export default {
  name: "FinanceiroConta",

  components: { BankAccountForm },

  data() {
    return { removing: false };
  },

  async mounted() {
    await this.$store.dispatch("doctor/getBankAccount");
  },

  methods: {
    openBankAccountModal() {
      this.$bvModal.show("bankAccount");
    },
    onBankAccountCreated() {
      this.$bvModal.hide("bankAccount");
    },
    async confirmDelete() {
      const result = await Swal.fire({
        title: "Remover conta bancária?",
        text: "Você poderá cadastrar uma nova conta após a remoção.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "var(--color-danger)",
        cancelButtonColor: "var(--color-gray-400)",
        confirmButtonText: "Sim, remover",
        cancelButtonText: "Cancelar"
      });
      if (result.isConfirmed) {
        this.removing = true;
        try {
          await this.$store.dispatch("doctor/deleteBankAccount");
          Swal.fire(
            "Removida!",
            "Conta bancária removida com sucesso.",
            "success"
          );
        } catch {
          Swal.fire({
            icon: "error",
            title: "Erro",
            text: "Tente novamente mais tarde."
          });
        } finally {
          this.removing = false;
        }
      }
    }
  },

  computed: {
    isLoading() {
      return this.$store.getters["doctor/loading"];
    },
    bankAccount() {
      return this.$store.state.doctor.bank_account;
    },
    hasBankAccount() {
      return !!this.bankAccount?.id;
    },
    userName() {
      const u =
        this.$store.state.user?.currentUser || this.$store.state.user?.user;
      return u?.name || u?.full_name || "—";
    },
    userCpf() {
      const u =
        this.$store.state.user?.currentUser || this.$store.state.user?.user;
      return u?.cpf || "—";
    }
  }
};
</script>

<style lang="scss" scoped>
.fin-body {
  padding: var(--space-6) var(--space-8);
  max-width: 880px;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
}

.tabs {
  margin-bottom: 0;
}

.fin-conta-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--space-4);
  align-items: start;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
}

// Cartão visual escuro
.fin-bank-card {
  background: linear-gradient(
    135deg,
    var(--color-gray-900),
    var(--color-gray-700)
  );
  border-radius: var(--radius-lg);
  padding: var(--space-5);
  color: #fff;
  box-shadow: var(--shadow-md);
}

.fin-bank-icon {
  background: rgba(255, 255, 255, 0.14) !important;
  color: #fff !important;
}

.fin-bank-name {
  font-size: var(--font-size-lg);
  font-weight: var(--fw-bold);
  margin-top: var(--space-5);
  margin-bottom: 2px;
}

.fin-bank-type {
  font-size: var(--font-size-sm);
  color: rgba(255, 255, 255, 0.7);
  margin-bottom: var(--space-5);
}

.fin-bank-details {
  display: flex;
  gap: var(--space-8);
}

.fin-bank-label {
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: rgba(255, 255, 255, 0.6);
  display: block;
}

.fin-bank-value {
  font-size: var(--font-size-lg);
  font-weight: var(--fw-bold);
  display: block;
}

// Form fields (DS-style)
.fieldset {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
}

.field {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);

  label {
    font-size: var(--font-size-sm);
    font-weight: var(--fw-semibold);
    color: var(--color-gray-700);
  }
}

.two-col {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--space-3);
}
</style>
